import { canReadDomain } from '@/api/core/authorization';
import { readEzremsUnitTransitionPreview } from '@/api/core/ezrems-unit-transition';
import type { FirstMoveInDateCommand } from '@/api/core/room-first-move-in-date';
import { changeUnitFirstMoveInDate } from '@/api/core/unit-first-move-in-date';
import { readUnitSaleEntry, type UnitSaleEntry } from '@/api/core/unit-sale';
import { isApiError } from '@/api/http/errors';
import { isUuid } from '@/lib/validation';
import { Button, ConfirmDialog, DatePicker, Input } from '@mgrvtechgroup/admin-ds';
import { useEffect, useRef, useState, useSyncExternalStore } from 'react';
import {
  beforeFirstDateBound,
  firstDateBound,
  firstDateBoundMessage,
  type FirstDateBound,
} from './first-date-lower-bound';
import { dateRefusalText, firstDateRefusal } from './first-date-refusal';
import { createHeldStore, type HeldUnitRequest } from './held-unit-requests';
import type { PropertyRuntime } from './property-queries';

/**
 * 슬롯(Unit) 첫 입주 가능일 변경. 로컬 BE candidate(010, 미배포) 계약을 따른다.
 * 기존 Room 날짜 PATCH에 실제 부모 Room 경로와 unit_id만 더하며, 현재 판매 revision이 CAS다.
 * 원 command·수행자를 고정하고, 결과가 불명확하면 자동 재전송하지 않는다.
 */
interface Props {
  runtime: PropertyRuntime;
  generation: number;
  propertyId: string;
  roomId: string;
  roomName: string;
  unit: { id: string; name: string };
  onChanged?: () => void;
  /** 이 슬롯의 보존 요청이 생기거나 사라졌을 때 알린다(다른 슬롯 화면의 안내 갱신용). */
  onHeldChange?: () => void;
  /** 아직 저장하지 않은 입력이 있는지 알린다(화면을 떠날 때 경고하는 데 쓴다). */
  onDirtyChange?: (dirty: boolean) => void;
}
interface Intent extends HeldUnitRequest {
  actorId: string;
  loaded: UnitSaleEntry;
  command: FirstMoveInDateCommand;
}
// 메모리에만 둔다: 화면을 다시 그려도 결과 불명 요청을 잃지 않는다(슬롯 단위).
const retained = createHeldStore<Intent>();
export const pendingUnitDateRequests = (runtime: PropertyRuntime, generation: number) =>
  retained.pending(
    runtime.client,
    runtime.apiNamespace,
    generation,
    access({ runtime, generation }),
  );
const access = ({ runtime, generation }: Pick<Props, 'runtime' | 'generation'>) => {
  const a = runtime.session.getSnapshot();
  return (
    a.generation === generation &&
    a.status === 'authorized' &&
    a.authorization !== null &&
    !a.deniedDomains.includes('booking') &&
    canReadDomain(a.authorization, 'booking', 32) &&
    canReadDomain(a.authorization, 'booking', 2)
  );
};
const money = (value: number | null) =>
  value === null
    ? '미설정(상속)'
    : value === 0
      ? '0원(명시 면제)'
      : `${value.toLocaleString('ko-KR')}원`;
const preserved = (before: UnitSaleEntry, after: UnitSaleEntry) =>
  (
    [
      'product',
      'channel',
      'daily_price_minor',
      'deposit_amount_minor',
      'cleaning_amount_minor',
    ] as const
  ).every((key) => before[key] === after[key]) && after.pending_outcome === null;

export function UnitFirstDateSection(props: Props) {
  const auth = useSyncExternalStore(
    props.runtime.session.subscribe,
    props.runtime.session.getSnapshot,
    props.runtime.session.getSnapshot,
  );
  const key = JSON.stringify([props.runtime.apiNamespace, props.generation, props.unit.id, auth]);
  return <FirstDate key={key} {...props} />;
}

function FirstDate(props: Props) {
  const { runtime, generation } = props;
  const [entry, setEntry] = useState<UnitSaleEntry | null>(null);
  // BR-04 하한: 전환 preview의 earliest_date. 읽지 못하면 하한을 모르는 것으로 두고 서버가 거절하는 결과를 그대로 보여 준다.
  const [bound, setBound] = useState<FirstDateBound | null>(null);
  const [held, setHeld] = useState<Intent | null>(
    () => retained.get(runtime.client, props.unit.id) ?? null,
  );
  const [reviewed, setReviewed] = useState<Intent | null>(null);
  const [date, setDate] = useState('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [feedback, setFeedback] = useState('');
  const alive = useRef(true);
  const lock = useRef(false);
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);
  useEffect(() => {
    props.onHeldChange?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- 보존 요청이 바뀔 때만 알린다.
  }, [held]);
  const dirty = !!entry && !held && (reason !== '' || date !== (entry.first_move_in_date ?? ''));
  useEffect(() => {
    props.onDirtyChange?.(dirty);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- 입력이 바뀔 때만 알린다.
  }, [dirty]);
  useEffect(
    () => () => {
      props.onDirtyChange?.(false);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- 화면이 사라질 때만 해제한다.
    [],
  );
  const valid = () => alive.current && access(props);
  const actor = async () => {
    const a = await runtime.client.get<{ user_id?: unknown }>('users/me', {
      headers: { 'Cache-Control': 'no-store' },
    });
    if (!valid() || !isUuid(a.user_id)) throw new Error('Actor unavailable');
    return a.user_id;
  };
  const readEntry = () =>
    readUnitSaleEntry(runtime.client, props.propertyId, props.roomId, props.unit.id);
  const readBoundStrict = async (): Promise<FirstDateBound> =>
    firstDateBound(await readEzremsUnitTransitionPreview(runtime.client, props.unit.id));
  const readBound = async (): Promise<FirstDateBound> => {
    try {
      return await readBoundStrict();
    } catch {
      return { kind: 'unknown' };
    }
  };
  const fail = (e: unknown, text: string) => {
    if (!alive.current) return;
    setFeedback(
      isApiError(e) && e.status === 404
        ? '슬롯 판매 정보를 찾을 수 없습니다. 이 서버가 슬롯 단위 판매를 지원하는지 확인하세요.'
        : text,
    );
    if (isApiError(e) && e.status === 403)
      runtime.session.reportDomainAccessDenied('booking', generation);
  };
  const read = async () => {
    if (!valid() || lock.current) return;
    lock.current = true;
    setBusy(true);
    setEntry(null);
    setBound(null);
    setReviewed(null);
    setFeedback('');
    try {
      const [fresh, freshBound] = await Promise.all([readEntry(), readBound()]);
      if (valid()) {
        setEntry(fresh);
        setBound(freshBound);
        setDate(fresh.first_move_in_date ?? '');
      }
    } catch (e) {
      fail(e, '슬롯 판매 현황을 확인하지 못했습니다.');
    } finally {
      lock.current = false;
      if (alive.current) setBusy(false);
    }
  };
  const eligible =
    entry !== null &&
    entry.allowed_actions.includes('unit.sales.first_date.change') &&
    entry.product === 'midterm' &&
    entry.channel === 'own_website' &&
    entry.pending_outcome === null &&
    entry.sale_revision !== null;
  const blockedReason = (() => {
    if (!entry) return '슬롯 판매 현황을 먼저 조회하세요.';
    if (entry.pending_outcome !== null) return '이전 판매 요청의 결과를 먼저 확인하세요.';
    if (entry.product !== 'midterm' || entry.channel !== 'own_website')
      return '미드텀으로 판매 중인 슬롯만 첫 입주 가능일을 바꿀 수 있습니다.';
    if (!entry.allowed_actions.includes('unit.sales.first_date.change'))
      return `현재 권한이나 계약 상태로는 첫 입주 가능일을 바꿀 수 없습니다.${entry.reasons.length ? ` (서버 사유 코드: ${entry.reasons.join(', ')})` : ''}`;
    if (!date || date === entry.first_move_in_date) return '현재와 다른 날짜를 선택하세요.';
    if (bound && beforeFirstDateBound(bound, date))
      return '선택한 날짜가 너무 이릅니다. 위 안내의 가장 이른 날부터 선택할 수 있습니다.';
    if (!reason.trim()) return '변경 사유를 입력하세요.';
    return '변경 내용을 검토할 수 있습니다.';
  })();
  const review = async () => {
    if (!valid() || lock.current || held || !entry || !eligible) return;
    lock.current = true;
    setBusy(true);
    setFeedback('');
    try {
      const actorId = await actor();
      const [fresh, freshBound] = await Promise.all([readEntry(), readBound()]);
      if (!valid() || (await actor()) !== actorId) throw new Error('Actor changed');
      if (
        JSON.stringify(fresh) !== JSON.stringify(entry) ||
        JSON.stringify(freshBound) !== JSON.stringify(bound)
      ) {
        setEntry(fresh);
        setBound(freshBound);
        throw new Error('Sale changed');
      }
      if (beforeFirstDateBound(freshBound, date)) throw new Error('Before the lower bound');
      if (fresh.sale_revision === null) throw new Error('No sale revision');
      setReviewed({
        namespace: runtime.apiNamespace,
        generation,
        unitId: props.unit.id,
        unitName: props.unit.name,
        roomName: props.roomName,
        actorId,
        loaded: fresh,
        command: {
          command_id: crypto.randomUUID(),
          expected_sale_revision: fresh.sale_revision,
          first_move_in_date: date,
          reason,
        },
      });
    } catch (e) {
      fail(e, '슬롯 판매 상태가 바뀌었습니다. 현재 값을 다시 확인한 뒤 검토하세요.');
    } finally {
      lock.current = false;
      if (alive.current) setBusy(false);
    }
  };
  const owns = (i: Intent) =>
    valid() &&
    i.namespace === runtime.apiNamespace &&
    i.generation === generation &&
    i.unitId === props.unit.id;
  const confirmResult = async (i: Intent, revision: number) => {
    const fresh = await readEntry();
    if ((await actor()) !== i.actorId || !owns(i)) throw new Error('Actor changed');
    if (
      fresh.sale_revision !== revision ||
      fresh.first_move_in_date !== i.command.first_move_in_date ||
      !preserved(i.loaded, fresh)
    )
      throw new Error('Readback mismatch');
    return fresh;
  };
  const save = async (i: Intent, replay: boolean) => {
    if (!owns(i) || lock.current) return;
    lock.current = true;
    setBusy(true);
    setFeedback('');
    let sent = false;
    try {
      if ((await actor()) !== i.actorId) throw new Error('Actor changed');
      if (!replay) {
        retained.set(runtime.client, i);
        setHeld(i);
        setReviewed(null);
      }
      sent = true;
      const receipt = await changeUnitFirstMoveInDate(
        runtime.client,
        props.roomId,
        i.unitId,
        i.command,
      );
      const fresh = await confirmResult(i, receipt.revision);
      retained.delete(runtime.client, i.unitId);
      setHeld(null);
      setEntry(fresh);
      setDate(fresh.first_move_in_date ?? '');
      setReason('');
      setFeedback('첫 입주 가능일 변경을 확인했습니다. 다시 조회한 슬롯 판매 현황과 일치합니다.');
      props.onChanged?.();
    } catch (e) {
      const definitive =
        sent &&
        !replay &&
        isApiError(e) &&
        e.kind === 'http' &&
        e.status !== undefined &&
        e.status >= 400 &&
        e.status < 500;
      if (definitive && retained.get(runtime.client, i.unitId) === i) {
        retained.delete(runtime.client, i.unitId);
        setHeld(null);
      }
      const refusal = definitive ? firstDateRefusal(e) : null;
      let reread = false;
      if (refusal !== null) {
        // 확정 거절이므로 읽기만으로 최신 판매 현황과 하한을 다시 맞춘다(입력은 그대로 둔다).
        try {
          const [fresh, freshBound] = await Promise.all([readEntry(), readBoundStrict()]);
          if (valid()) {
            setEntry(fresh);
            setBound(freshBound);
            reread = true;
          }
        } catch {
          // 다시 읽지 못해도 거절 안내는 그대로 보여 준다.
        }
      }
      fail(
        e,
        definitive
          ? refusal === null
            ? '서버가 요청을 거절했습니다. 입력과 권한을 확인하세요.'
            : dateRefusalText(refusal, reread ? 'reread' : 'not-reread')
          : '변경 결과가 불명확합니다. 처음 요청을 보존했으며 자동 재전송하지 않습니다.',
      );
    } finally {
      lock.current = false;
      if (alive.current) setBusy(false);
    }
  };
  // 현재 판매 현황 확인은 읽기뿐이라, 같은 namespace·같은 수행자(users/me로 확인)이면 로그인 generation이 바뀐 뒤에도 허용한다.
  const ownsRead = (i: Intent) =>
    valid() && i.namespace === runtime.apiNamespace && i.unitId === props.unit.id;
  const inspect = async (i: Intent) => {
    if (!ownsRead(i) || lock.current) return;
    lock.current = true;
    setBusy(true);
    setFeedback('');
    try {
      if ((await actor()) !== i.actorId) throw new Error('Actor changed');
      const fresh = await readEntry();
      if ((await actor()) !== i.actorId || !ownsRead(i)) throw new Error('Actor changed');
      setEntry(fresh);
      if (
        fresh.first_move_in_date === i.command.first_move_in_date &&
        fresh.sale_revision !== null &&
        fresh.sale_revision > i.command.expected_sale_revision &&
        preserved(i.loaded, fresh)
      ) {
        retained.delete(runtime.client, i.unitId);
        setHeld(null);
        setReason('');
        setDate(i.command.first_move_in_date);
        setFeedback(
          '현재 판매 현황이 원래 요청의 날짜와 같은 것을 확인했습니다. 다른 변경이 같은 날짜를 만든 경우는 구분할 수 없습니다.',
        );
        props.onChanged?.();
      } else {
        setFeedback(
          '현재 판매 현황에서 원래 요청의 반영을 확인하지 못했습니다. 처음 요청을 보존했습니다.',
        );
      }
    } catch (e) {
      fail(e, '현재 판매 현황을 확인하지 못했습니다. 처음 요청을 보존했습니다.');
    } finally {
      lock.current = false;
      if (alive.current) setBusy(false);
    }
  };
  const allowed = access(props);
  const ownHeld = held?.namespace === runtime.apiNamespace && held.unitId === props.unit.id;
  const sameSession = ownHeld && held.generation === generation;
  const blocked = busy || !!held || !!reviewed;
  return (
    <section
      aria-label="슬롯 판매 현황과 첫 입주 가능일"
      className="body-sm space-y-4 rounded-md border p-4 leading-relaxed"
    >
      <h4 className="font-semibold">
        {props.roomName} · {props.unit.name} 슬롯 판매 현황
      </h4>
      <Button
        className="h-auto min-h-8 whitespace-normal"
        variant="outline"
        disabled={!allowed || busy}
        onClick={() => void read()}
      >
        슬롯 판매 현황 조회
      </Button>
      {!allowed && <p>슬롯 판매 조회에 Booking 판매 권한이 필요합니다.</p>}
      {entry && (
        <div role="status" className="space-y-1">
          <p>
            판매 상품: {entry.product ?? '미설정'} · 채널: {entry.channel ?? '미설정'} · 상태:{' '}
            {entry.status}
          </p>
          <p>첫 입주 가능일: {entry.first_move_in_date ?? '미정'}</p>
          <p>
            일 가격:{' '}
            {entry.daily_price_minor === null
              ? '미설정'
              : `${entry.daily_price_minor.toLocaleString('ko-KR')}원`}{' '}
            · 보증금: {money(entry.deposit_amount_minor)} · 청소비:{' '}
            {money(entry.cleaning_amount_minor)}
          </p>
          <p>
            남은 기존 예약: {entry.residual_booking_count}건 · 미해결 판매 요청:{' '}
            {entry.pending_outcome ?? '없음'}
          </p>
        </div>
      )}
      {entry && !held && (
        <>
          <div className="grid gap-1">
            <p>새 첫 입주 가능일</p>
            {bound && <p className="text-muted-foreground">{firstDateBoundMessage(bound)}</p>}
            <DatePicker
              value={date ? new Date(`${date}T00:00:00`) : undefined}
              onChange={(value) => {
                setDate(
                  value
                    ? `${String(value.getFullYear())}-${String(value.getMonth() + 1).padStart(2, '0')}-${String(value.getDate()).padStart(2, '0')}`
                    : '',
                );
              }}
            />
          </div>
          <label className="grid gap-1">
            첫 입주 가능일 변경 사유
            <Input
              aria-label="슬롯 첫 입주일 변경 사유"
              value={reason}
              disabled={blocked}
              maxLength={500}
              onChange={(e) => {
                setReason(e.target.value);
              }}
            />
          </label>
          <p id="unit-first-date-block" role="status">
            {blockedReason}
          </p>
          <Button
            aria-describedby="unit-first-date-block"
            className="h-auto min-h-8 whitespace-normal"
            disabled={
              !allowed ||
              blocked ||
              !eligible ||
              (bound !== null && beforeFirstDateBound(bound, date)) ||
              !date ||
              date === entry.first_move_in_date ||
              !reason.trim() ||
              Array.from(reason).length > 500
            }
            onClick={() => void review()}
          >
            첫 입주일 변경 검토
          </Button>
        </>
      )}
      <ConfirmDialog
        open={!!reviewed}
        onOpenChange={(open) => {
          if (!open) setReviewed(null);
        }}
        target={`${props.roomName} · ${props.unit.name} 슬롯`}
        action="첫 입주일 변경"
        consequence={`첫 입주 가능일 ${reviewed?.loaded.first_move_in_date ?? '미정'} → ${reviewed?.command.first_move_in_date ?? ''} · 사유 ${reviewed?.command.reason ?? ''}. 이 슬롯만 바뀌며 기존 예약과 같은 객실의 다른 슬롯은 바뀌지 않습니다.`}
        variant="default"
        onConfirm={() => {
          if (reviewed) void save(reviewed, false);
        }}
      />
      {held && (
        <>
          <p role="alert">
            원래 첫 입주일 변경 요청을 보존했습니다. 새 요청은 차단하고 자동 재전송하지 않습니다.
            {sameSession && allowed && (
              <>
                {' '}
                대상: {held.unitName} 슬롯 · 날짜: {held.command.first_move_in_date}.
              </>
            )}
          </p>
          {ownHeld && !sameSession && (
            <p role="status">
              이전 로그인에서 접수한 요청입니다. 같은 수행자로 확인되면 현재 판매 현황을 조회할 수
              있고, 같은 요청을 다시 보내는 것은 현재 로그인에서는 막혀 있습니다.
            </p>
          )}
          {ownHeld && (
            <div className="flex flex-wrap items-center gap-2">
              {sameSession && (
                <Button
                  className="h-auto min-h-8 whitespace-normal"
                  disabled={!allowed || busy}
                  onClick={() => void save(held, true)}
                >
                  같은 요청 다시 시도
                </Button>
              )}
              <Button
                className="h-auto min-h-8 whitespace-normal"
                disabled={!allowed || busy}
                onClick={() => void inspect(held)}
              >
                현재 판매 현황으로 확인
              </Button>
            </div>
          )}
        </>
      )}
      {feedback && <p role="status">{feedback}</p>}
    </section>
  );
}
